import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import dayjs from 'dayjs';
import { Department } from 'src/app/models/department.model';
import { PartnerContract } from 'src/app/models/partner-contract.model';
import { PartnerContractVariableCondition } from 'src/app/models/partner-contract-variable-condition.model';
import { ApiService } from 'src/app/services/api.service';
import { PartnerContractDialogComponent, PartnerContractDialogData } from './partner-contract-dialog.component';
import {
  PartnerContractVariableConditionDialogComponent,
  PartnerContractVariableConditionDialogData,
} from './partner-contract-variable-condition-dialog.component';

@Component({
  selector: 'app-partner-contracts-list',
  templateUrl: './partner-contracts-list.component.html',
  styleUrls: ['./partner-contracts-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, MatTableModule, MatButtonModule, MatIconModule, MatMenuModule, MatDialogModule],
})
export class PartnerContractsListComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly route = inject(ActivatedRoute);
  private readonly destroy_ref = inject(DestroyRef);

  readonly displayed_columns = ['status', 'start_date', 'end_date', 'departments', 'actions'];
  readonly data_source = new MatTableDataSource<PartnerContractRow>([]);
  readonly departments = signal<Department[]>([]);

  private user_id = 0;

  ngOnInit(): void {
    this.user_id = parseInt(this.route.parent?.snapshot.paramMap.get('id') as string, 10);
    this.api
      .getDepartments()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(response => {
        this.departments.set(response.data ?? []);
      });
    this.fetchContracts();
  }

  openCreateDialog(): void {
    const data: PartnerContractDialogData = {
      user_id: this.user_id,
      departments: this.departments(),
    };

    this.dialog
      .open(PartnerContractDialogComponent, { width: '560px', data })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(result => {
        if (result) {
          this.fetchContracts();
        }
      });
  }

  openConditionDialog(contract: PartnerContract, condition?: PartnerContractVariableCondition): void {
    const data: PartnerContractVariableConditionDialogData = {
      contract_id: contract.id,
      condition: condition ?? null,
      departments: this.departments(),
    };

    this.dialog
      .open(PartnerContractVariableConditionDialogComponent, { width: '560px', data })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(result => {
        if (result) {
          this.fetchContracts();
        }
      });
  }

  deleteContract(contract: PartnerContract): void {
    if (!confirm('¿Eliminar este contrato de partner?')) {
      return;
    }

    this.api
      .deleteUserPartnerContract(this.user_id, contract.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(() => this.fetchContracts());
  }

  deleteCondition(contract: PartnerContract, condition: PartnerContractVariableCondition): void {
    if (!confirm('¿Eliminar esta condición variable?')) {
      return;
    }

    this.api
      .deletePartnerContractVariableCondition(contract.id, condition.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(() => this.fetchContracts());
  }

  private fetchContracts(): void {
    this.api
      .getUserPartnerContracts(this.user_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(response => {
        this.data_source.data = (response.data ?? []).map(contract => ({
          ...contract,
          is_active: this.isContractActive(contract),
          departments_label: this.departmentsLabel(contract),
        }));
      });
  }

  private isContractActive(contract: PartnerContract): boolean {
    const now = dayjs();
    const start = dayjs(contract.start_date);
    const end = contract.end_date ? dayjs(contract.end_date) : null;
    return (start.isBefore(now) || start.isSame(now)) && (end == null || end.isAfter(now) || end.isSame(now));
  }

  private departmentsLabel(contract: PartnerContract): string {
    const condition =
      contract.partner_contract_variable_conditions?.find(vc => vc.is_active) ??
      contract.current_partner_contract_variable_condition ??
      contract.partner_contract_variable_conditions?.[0];

    const names = condition?.departments?.map(d => d.name) ?? [];

    return names.length > 0 ? names.join(', ') : '—';
  }
}

type PartnerContractRow = PartnerContract & {
  is_active: boolean;
  departments_label: string;
};
